import { Trans } from "@lingui/react/macro";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Navigate, Outlet, RouteObject, useLocation } from "react-router-dom";

import {
  AdminGuard,
  countryPermissionCheckFn,
  PermissionGuard,
  useHasPermission,
} from "@/common/acl/guard/guard";
import { Failed } from "@/common/components/info/info";
import { Spinner } from "@/common/components/spinner/spinner";

import * as Companies from "./admin/companies/companies";
import * as Company from "./admin/company/company";
import { Layout as AdminLayout } from "./admin/components/layout/layout";
import { Loader } from "./admin/components/loader/loader";
import { path as userPath } from "./admin/components/user/user";
import { EditorProvider, path as editorPath } from "./admin/editor/editor";
import * as Payments from "./admin/payments/payments";
import { PendingUser } from "./admin/pending-users/pending-user";
import * as PendingUsers from "./admin/pending-users/pending-users";
import * as User from "./admin/users/user";
import * as Users from "./admin/users/users";

const routes = (): RouteObject[] => [
  {
    children: [
      {
        children: [
          {
            children: [
              {
                element: (
                  <PermissionGuard
                    onDenied={<Navigate to=".." />}
                    permission="news:report:manage"
                  >
                    <EditorProvider />
                  </PermissionGuard>
                ),
                path: editorPath,
              },
              {
                element: (
                  <PermissionGuard
                    onDenied={<Navigate to=".." />}
                    permission="news:report:manage"
                  >
                    <EditorProvider />
                  </PermissionGuard>
                ),
                path: `${editorPath}/:id`,
              },
              {
                children: [
                  {
                    element: (
                      <Loader>
                        <Company.Company>
                          <Company.Pricing />
                          <Company.Subscriptions />
                          <PermissionGuard permission="users:company:plan">
                            <Company.SubscriptionForm />
                          </PermissionGuard>
                          <Company.Detail />
                          <Company.Users />
                        </Company.Company>
                      </Loader>
                    ),
                    path: "company/:id",
                  },
                ],
                element: (
                  <PermissionGuard
                    onDenied={<Navigate to=".." />}
                    permission="users:*:list"
                  >
                    <PermissionGuard
                      onDenied={
                        <p className="mt-8 text-center">
                          <Trans>
                            Nemáte právo spravovat systém v žádné ze zemí, které
                            podporujeme. Obraťte se prosím na administrátora.
                          </Trans>
                        </p>
                      }
                      permissionCheckFn={countryPermissionCheckFn}
                    >
                      <ErrorBoundary
                        fallbackRender={({ error }) => <Failed error={error} />}
                      >
                        <Suspense fallback={<Spinner className="mt-4" />}>
                          <Payments.Payments />
                        </Suspense>
                      </ErrorBoundary>
                    </PermissionGuard>
                  </PermissionGuard>
                ),

                path: Payments.path,
              },
              {
                children: [
                  {
                    element: (
                      <Loader>
                        <Company.Company>
                          <Company.Pricing />
                          <Company.Subscriptions />
                          <PermissionGuard permission="users:company:plan">
                            <Company.SubscriptionForm />
                          </PermissionGuard>
                          <Company.Detail />
                          <Company.Users />
                        </Company.Company>
                      </Loader>
                    ),
                    path: "company/:id",
                  },
                  {
                    element: (
                      <Loader>
                        <User.User />
                      </Loader>
                    ),
                    path: userPath,
                  },
                ],
                element: (
                  <PermissionGuard
                    onDenied={<Navigate to=".." />}
                    permission="users:*:list"
                  >
                    <PermissionGuard
                      onDenied={
                        <p className="mt-8 text-center">
                          <Trans>
                            Nemáte právo spravovat systém v žádné ze zemí, které
                            podporujeme. Obraťte se prosím na administrátora.
                          </Trans>
                        </p>
                      }
                      permissionCheckFn={countryPermissionCheckFn}
                    >
                      <ErrorBoundary
                        fallbackRender={({ error }) => <Failed error={error} />}
                      >
                        <Suspense fallback={<Spinner className="mt-4" />}>
                          <Users.Users />
                        </Suspense>
                      </ErrorBoundary>
                    </PermissionGuard>
                  </PermissionGuard>
                ),
                path: Users.path,
              },
              {
                children: [
                  {
                    element: (
                      <Loader>
                        <Company.Company>
                          <Company.Pricing />
                          <Company.Subscriptions />
                          <PermissionGuard permission="users:company:plan">
                            <Company.SubscriptionForm />
                          </PermissionGuard>
                          <Company.Detail />
                          <Company.Users />
                        </Company.Company>
                      </Loader>
                    ),
                    path: ":id",
                  },
                ],
                element: (
                  <PermissionGuard
                    onDenied={<Navigate to=".." />}
                    permission="users:*:list"
                  >
                    <PermissionGuard
                      onDenied={
                        <p className="mt-8 text-center">
                          <Trans>
                            Nemáte právo spravovat systém v žádné ze zemí, které
                            podporujeme. Obraťte se prosím na administrátora.
                          </Trans>
                        </p>
                      }
                      permissionCheckFn={countryPermissionCheckFn}
                    >
                      <ErrorBoundary
                        fallbackRender={({ error }) => <Failed error={error} />}
                      >
                        <Suspense fallback={<Spinner className="mt-4" />}>
                          <Companies.Companies />
                        </Suspense>
                      </ErrorBoundary>
                    </PermissionGuard>
                  </PermissionGuard>
                ),
                path: Companies.path,
              },
              {
                children: [
                  {
                    element: (
                      <Loader>
                        <PendingUser />
                      </Loader>
                    ),
                    path: userPath,
                  },
                  {
                    element: (
                      <Loader>
                        <Company.Company>
                          <PermissionGuard permission="users:company:plan">
                            <Company.SubscriptionForm />
                          </PermissionGuard>
                          <Company.Detail />
                          <Company.Users />
                        </Company.Company>
                      </Loader>
                    ),
                    path: Company.path,
                  },
                ],
                element: (
                  <PermissionGuard
                    onDenied={<Navigate to=".." />}
                    permission="users:*:list"
                  >
                    <PermissionGuard
                      onDenied={
                        <p className="mt-8 text-center">
                          <Trans>
                            Nemáte právo spravovat systém v žádné ze zemí, které
                            podporujeme. Obraťte se prosím na administrátora.
                          </Trans>
                        </p>
                      }
                      permissionCheckFn={countryPermissionCheckFn}
                    >
                      <PendingUsers.Users />
                    </PermissionGuard>
                  </PermissionGuard>
                ),
                path: PendingUsers.path,
              },
            ],
            element: <AdminLayout />,
            path: "",
          },
        ],
        element: <AdminGuard />,
      },
    ],
    element: <RedirectToAvailableScreen />,
    path: "admin",
  },
];

const RedirectToAvailableScreen = () => {
  const { pathname } = useLocation();
  const canEditNews = useHasPermission("news:report:manage");
  const canManageUsersOrCompanies = useHasPermission("users:*:list");

  if (
    [canEditNews, canManageUsersOrCompanies].some(
      (permission) => permission === null,
    )
  ) {
    return null;
  }

  if (pathname === "/admin" || pathname === "/admin/") {
    let target: string | undefined;

    if (canManageUsersOrCompanies) {
      target = PendingUsers.path;
    }

    if (canEditNews) {
      target = editorPath;
    }

    if (!target) {
      return <Navigate to="/" />;
    }

    return <Navigate replace to={target} />;
  }

  return <Outlet />;
};

export { routes };
